import {
  isTrpcFetchAbortError,
  isTrpcUnauthorizedError,
} from '@risksmart-app/components/src/utils/trpc/classification';
import type { Query } from '@tanstack/react-query';

/**
 * Whether a portal query failure should be sent to Sentry.
 *
 * Navigating away aborts the in-flight fetch, which surfaces as a
 * `TRPCClientError` wrapping a "Failed to fetch"/AbortError. That is a normal
 * lifecycle event, not a failure worth reporting, so the QueryCache reporter
 * drops it when the query has no observers left.
 *
 * The abort classification itself is shared with web — both apps are the same
 * browser → tRPC hop (COI-159). The portal used to keep a byte-identical copy
 * of the three helpers behind `isTrpcFetchAbortError`; what it did NOT copy was
 * the retry policy above them, which is how it ended up retrying every 403
 * three times on react-query's defaults.
 *
 * A 401 is also suppressed because the auth redirect link already owns it: it
 * clears the session and sends the browser through /logout. Reporting that
 * expected lifecycle response would create a duplicate event for each query
 * that happened to be in flight. Other 4xx and 5xx failures still report.
 *
 * Web's equivalent is deliberately wider: it also suppresses during a document
 * unload and honours a per-query opt-out. Neither has a consumer here yet.
 */
export const shouldReportTrpcQueryError = (
  error: unknown,
  query: Pick<Query, 'getObserversCount'>
): boolean =>
  !isTrpcUnauthorizedError(error) &&
  !(query.getObserversCount() === 0 && isTrpcFetchAbortError(error));
